import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useNodesState,
  useEdgesState,
  addEdge,
  type Node,
  type Edge,
  type OnConnect,
} from "@xyflow/react";
import { Plus, Webhook } from "lucide-react";
import { toast } from "@/shared/components/toast";
import { useAutomations } from "../hooks/useAutomations";
import { useNodeCatalog } from "../hooks/useNodeCatalog";
import { useWorkflowExecution } from "../hooks/useWorkflowExecution";
import { useLastExecutionData } from "../hooks/useLastExecutionData";
import type WorkflowNode from "../models/WorkflowNode";
import type { EdgeKind } from "../models/WorkflowEdge";
import type TriggerDefinition from "../models/TriggerDefinition";
import type NodeDescriptor from "../models/NodeDescriptor";
import type WorkflowDefinition from "../models/WorkflowDefinition";
import type { CreateWorkflowRequest } from "../models/WorkflowDefinition";
import AutomationCanvas from "../components/canvas/AutomationCanvas";
import NodePalette from "../components/canvas/NodePalette";
import NodeConfigPanel from "../components/canvas/NodeConfigPanel";
import EdgeConfigPanel from "../components/canvas/EdgeConfigPanel";
import WebhookPanel from "../components/canvas/WebhookPanel";
import CanvasToolbar from "../components/canvas/CanvasToolbar";
import ExecutionPanel from "../components/canvas/ExecutionPanel";
import ExecutionHistoryPanel from "../components/canvas/ExecutionHistoryPanel";
import type { WorkflowNodeData, WorkflowEdgeData } from "../components/canvas/types";
import { readLayout, writeLayout, type NodeLayout } from "../lib/layoutStorage";
import { recordRecentNode } from "../lib/recentNodesStorage";

const DEFAULT_TRIGGER: TriggerDefinition = { type: "Manual", requiresIdempotencyKey: false };

/**
 * Resolves the catalog descriptor for a saved node. Every integration provider shares the same
 * `nodeType` ("Integration"), so those are disambiguated by `properties.providerId` instead.
 */
function resolveDescriptor(catalog: NodeDescriptor[], workflowNode: WorkflowNode): NodeDescriptor | undefined {
  if (workflowNode.type === "Integration") {
    const providerId = workflowNode.properties?.providerId;
    return catalog.find((d) => d.nodeType === "Integration" && d.providerId === providerId);
  }
  return catalog.find((d) => d.nodeType === workflowNode.type);
}

function buildInitialGraph(
  workflow: WorkflowDefinition | null,
  catalog: NodeDescriptor[],
  workflowId: string,
): { nodes: Node<WorkflowNodeData>[]; edges: Edge<WorkflowEdgeData>[] } {
  const layout = readLayout(workflowId);
  const incomingTargets = new Set((workflow?.edges ?? []).map((e) => e.targetNodeId));

  const nodes: Node<WorkflowNodeData>[] = (workflow?.nodes ?? []).map((workflowNode, index) => ({
    id: workflowNode.id,
    type: "automationNode",
    position: layout[workflowNode.id] ?? { x: 280, y: 100 + index * 180 },
    data: {
      workflowNode,
      descriptor: resolveDescriptor(catalog, workflowNode),
      isEntryNode: !incomingTargets.has(workflowNode.id),
    },
  }));

  const edges: Edge<WorkflowEdgeData>[] = (workflow?.edges ?? []).map((workflowEdge) => ({
    id: workflowEdge.id,
    type: "automationEdge",
    source: workflowEdge.sourceNodeId,
    target: workflowEdge.targetNodeId,
    data: { condition: workflowEdge.condition, kind: workflowEdge.kind },
  }));

  return { nodes, edges };
}

export default function AutomationEditorPage() {
  const { workflowId: paramWorkflowId } = useParams<{ workflowId: string }>();
  const navigate = useNavigate();
  const isNew = !paramWorkflowId;
  const [workflowId] = useState(() => paramWorkflowId ?? crypto.randomUUID());

  const { getCatalog, loading: catalogLoading } = useNodeCatalog();
  const { getWorkflow, publish } = useAutomations();
  const { run, status: executionStatus, polling } = useWorkflowExecution();
  const executionData = useLastExecutionData(workflowId, !isNew);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<WorkflowNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<WorkflowEdgeData>>([]);
  const [catalog, setCatalog] = useState<NodeDescriptor[]>([]);
  const [name, setName] = useState("Nueva automatización");
  const [trigger, setTrigger] = useState<TriggerDefinition>(DEFAULT_TRIGGER);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [showExecutionPanel, setShowExecutionPanel] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Una automatización nueva es tuya por definición; una existente lo dice el backend.
  // Sin propiedad solo se puede ver y ejecutar: publicar y tocar el webhook devuelven 403.
  const [isOwner, setIsOwner] = useState(true);
  // El dashboard cuelga del workspace, no de la automatización: sin workspace no hay a dónde ir.
  const [workspace, setWorkspace] = useState<{ id: string; name: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const [catalogResult, workflowResult] = await Promise.all([
          getCatalog(),
          isNew ? Promise.resolve(null) : getWorkflow(workflowId),
        ]);
        if (ignore) return;

        const resolvedCatalog = catalogResult ?? [];
        setCatalog(resolvedCatalog);

        if (!isNew && !workflowResult) {
          toast.error("No se encontró la automatización solicitada.");
          navigate("/admin/automations", { replace: true });
          return;
        }

        const workflow = workflowResult ?? null;
        if (workflow) {
          setName(workflow.name);
          setTrigger(workflow.trigger);
          setIsOwner(workflow.isOwner);
          setWorkspace(
            workflow.workspaceId
              ? { id: workflow.workspaceId, name: workflow.workspaceName ?? "Workspace" }
              : null,
          );
        }

        const initial = buildInitialGraph(workflow, resolvedCatalog, workflowId);
        setNodes(initial.nodes);
        setEdges(initial.edges);
      } catch {
        // error toast already shown by useAsync
      }
    })();
    return () => {
      ignore = true;
    };
  }, [isNew, workflowId, getCatalog, getWorkflow, navigate, setNodes, setEdges]);

  // A node is the entry point when it has no incoming edges; recompute on every connection change.
  useEffect(() => {
    const targets = new Set(edges.map((e) => e.target));
    setNodes((nds) =>
      nds.map((n) => {
        const isEntryNode = !targets.has(n.id);
        if (n.data.isEntryNode === isEntryNode) return n;
        return { ...n, data: { ...n.data, isEntryNode } };
      }),
    );
  }, [edges, setNodes]);

  const onConnect: OnConnect = useCallback(
    (connection) => {
      const sourceNode = nodes.find((n) => n.id === connection.source)?.data.workflowNode;
      setEdges((eds) => {
        // Al conectar desde un Switch, asignar automáticamente la primera ruta libre
        // para que la rama funcione sin tener que escribir la expresión a mano.
        let condition = "";
        if (sourceNode?.type === "Switch" && connection.source) {
          const rawCases = sourceNode.properties?.cases;
          const keys = [
            ...(Array.isArray(rawCases)
              ? rawCases
                  .map((c) => String((c as Record<string, unknown>).key ?? ""))
                  .filter(Boolean)
              : []),
            String(sourceNode.properties?.defaultCaseKey ?? "default"),
          ];
          const used = new Set(
            eds.filter((e) => e.source === connection.source).map((e) => e.data?.condition ?? ""),
          );
          const free = keys.find(
            (k) => !used.has(`{{ nodes.${connection.source}.output.${k} }}`),
          );
          if (free) condition = `{{ nodes.${connection.source}.output.${free} }}`;
        }
        return addEdge(
          { ...connection, id: crypto.randomUUID(), data: { condition, kind: "Normal" } },
          eds,
        );
      });
    },
    [setEdges, nodes],
  );

  const onDropNode = useCallback(
    (descriptor: NodeDescriptor, position: { x: number; y: number }) => {
      const id = crypto.randomUUID();
      // Integration nodes share nodeType "Integration"; `providerId` is what disambiguates them
      // (see resolveDescriptor). It's a schema `const`, so the properties form never writes it —
      // seed it here or the node loses its provider identity once saved and reloaded.
      const properties: Record<string, unknown> =
        descriptor.nodeType === "Integration" && descriptor.providerId
          ? { providerId: descriptor.providerId }
          : {};
      const workflowNode: WorkflowNode = {
        id,
        type: descriptor.nodeType,
        name: descriptor.displayName,
        properties,
        inputSchema: "",
        outputSchema: "",
        isAiNode: false,
      };
      const newNode: Node<WorkflowNodeData> = {
        id,
        type: "automationNode",
        position,
        data: { workflowNode, descriptor, isEntryNode: true },
      };
      setNodes((nds) => [...nds, newNode]);
      recordRecentNode(descriptor);
      setPaletteOpen(false);
    },
    [setNodes],
  );

  const handleNodeDataChange = useCallback(
    (updated: WorkflowNode) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === updated.id ? { ...n, data: { ...n.data, workflowNode: updated } } : n)),
      );
    },
    [setNodes],
  );

  /**
   * Clears the config-panel state AND React Flow's own `selected` flags — clearing only
   * the ids leaves the node visually ringed on canvas with no panel open.
   */
  const clearSelection = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setNodes((nds) => nds.map((n) => (n.selected ? { ...n, selected: false } : n)));
    setEdges((eds) => eds.map((e) => (e.selected ? { ...e, selected: false } : e)));
  }, [setNodes, setEdges]);

  const handleDeleteNode = useCallback(() => {
    if (!selectedNodeId) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNodeId && e.target !== selectedNodeId));
    setSelectedNodeId(null);
  }, [selectedNodeId, setNodes, setEdges]);

  const handleEdgeDataChange = useCallback(
    (data: { condition: string; kind: EdgeKind }) => {
      if (!selectedEdgeId) return;
      setEdges((eds) => eds.map((e) => (e.id === selectedEdgeId ? { ...e, data } : e)));
    },
    [selectedEdgeId, setEdges],
  );

  const handleDeleteEdge = useCallback(() => {
    if (!selectedEdgeId) return;
    setEdges((eds) => eds.filter((e) => e.id !== selectedEdgeId));
    setSelectedEdgeId(null);
  }, [selectedEdgeId, setEdges]);

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (nodes.length === 0) {
      toast.error("Agrega al menos un nodo antes de guardar.");
      return false;
    }

    const entryCount = nodes.filter((n) => n.data.isEntryNode).length;
    if (entryCount === 0) {
      toast.warning("No hay un nodo de inicio: agrega un nodo sin conexiones entrantes.");
    } else if (entryCount > 1) {
      toast.warning("Hay más de un posible nodo de inicio; la ejecución podría fallar.");
    }

    setSaving(true);
    try {
      const request: CreateWorkflowRequest = {
        WorkflowId: workflowId,
        Name: name,
        Trigger: trigger,
        Nodes: nodes.map((n) => n.data.workflowNode),
        Edges: edges.map((e) => ({
          id: e.id,
          sourceNodeId: e.source,
          targetNodeId: e.target,
          condition: e.data?.condition ?? "",
          kind: e.data?.kind ?? "Normal",
        })),
      };
      await publish(request);

      const layout: Record<string, NodeLayout> = {};
      nodes.forEach((n) => {
        layout[n.id] = n.position;
      });
      writeLayout(workflowId, layout);

      if (isNew) {
        navigate(`/admin/automations/${workflowId}`, { replace: true });
      }
      return true;
    } catch {
      return false;
    } finally {
      setSaving(false);
    }
  }, [nodes, edges, workflowId, name, trigger, publish, isNew, navigate]);

  const handleRun = useCallback(async () => {
    const saved = await handleSave();
    if (!saved) return;

    setRunning(true);
    setShowExecutionPanel(true);
    try {
      await run(workflowId);
      // Refresca las muestras del picker de datos con los outputs de esta ejecución.
      executionData.refresh();
    } catch {
      // error toast already shown by useWorkflowExecution
    } finally {
      setRunning(false);
    }
  }, [handleSave, run, workflowId, executionData]);

  const selectedNode = nodes.find((n) => n.id === selectedNodeId);
  const selectedEdge = edges.find((e) => e.id === selectedEdgeId);

  return (
    <div className="flex flex-col h-full bg-[#f0f0f3]">
      <CanvasToolbar
        name={name}
        trigger={trigger}
        onNameChange={setName}
        onTriggerChange={setTrigger}
        onSave={handleSave}
        onRun={handleRun}
        readOnly={!isOwner}
        onOpenDashboard={
          isNew || !workspace
            ? undefined
            : () => navigate(`/admin/workspaces/${workspace.id}/dashboard`)
        }
        dashboardLabel={workspace ? `Dashboard de ${workspace.name}` : undefined}
        onShowHistory={
          isNew
            ? undefined
            : () => {
                // A node/edge config panel hides the history aside; "Historial" must win
                // over it or the button appears to do nothing.
                if (selectedNodeId || selectedEdgeId) {
                  clearSelection();
                  setShowHistory(true);
                } else {
                  setShowHistory((s) => !s);
                }
              }
        }
        saving={saving}
        running={running}
      />

      <div className="flex flex-1 overflow-hidden">
        <div className="relative flex-1 flex">
          <button
            onClick={() => setPaletteOpen((open) => !open)}
            className="absolute top-4 left-4 z-20 flex items-center gap-2 h-9 px-4 rounded-xl bg-accent text-white text-sm font-semibold shadow-lg shadow-accent/25 hover:bg-accent/90 hover:shadow-accent/35 transition-all cursor-pointer"
          >
            <Plus className="size-4" />
            Agregar nodo
          </button>

          <NodePalette
            catalog={catalog}
            loading={catalogLoading}
            open={paletteOpen}
            onClose={() => setPaletteOpen(false)}
          />

          <AutomationCanvas
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(nodeId) => {
              setSelectedNodeId(nodeId);
              setSelectedEdgeId(null);
            }}
            onEdgeClick={(edgeId) => {
              setSelectedEdgeId(edgeId);
              setSelectedNodeId(null);
            }}
            onPaneClick={clearSelection}
            onDropNode={onDropNode}
            onDelete={() => {
              setSelectedNodeId(null);
              setSelectedEdgeId(null);
            }}
          />
        </div>

        {selectedNode && (
          <NodeConfigPanel
            node={selectedNode.data.workflowNode}
            descriptor={selectedNode.data.descriptor}
            isEntryNode={selectedNode.data.isEntryNode}
            executionData={isNew ? undefined : executionData}
            nodeNames={Object.fromEntries(nodes.map((n) => [n.id, n.data.workflowNode.name]))}
            upstreamNodeId={
              edges.find(
                (e) => e.target === selectedNode.id && (e.data?.kind ?? "Normal") === "Normal",
              )?.source
            }
            onChange={handleNodeDataChange}
            onDelete={handleDeleteNode}
            onClose={clearSelection}
          />
        )}

        {selectedEdge && (
          <EdgeConfigPanel
            condition={selectedEdge.data?.condition ?? ""}
            kind={selectedEdge.data?.kind ?? "Normal"}
            sourceNode={nodes.find((n) => n.id === selectedEdge.source)?.data.workflowNode}
            onChange={handleEdgeDataChange}
            onDelete={handleDeleteEdge}
            onClose={clearSelection}
          />
        )}

        {showHistory && !selectedNodeId && !selectedEdgeId && (
          <ExecutionHistoryPanel
            workflowId={workflowId}
            nodeNames={Object.fromEntries(nodes.map((n) => [n.id, n.data.workflowNode.name]))}
            onClose={() => setShowHistory(false)}
          />
        )}

        {trigger.type === "Webhook" && isOwner && !selectedNodeId && !selectedEdgeId && !showHistory && (
          <aside className="w-96 shrink-0 border-l border-black/5 bg-white/70 backdrop-blur-md overflow-y-auto">
            <div className="sticky top-0 z-10 flex items-center gap-3 px-5 py-3.5 bg-white/85 backdrop-blur-md border-b border-black/5">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-600 shadow-sm">
                <Webhook className="size-5" />
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-bold text-foreground truncate">Webhook</h2>
                <p className="text-[11px] text-foreground/40 font-medium truncate">
                  Configuración del disparador
                </p>
              </div>
            </div>
            <div className="p-5 space-y-5">
              <WebhookPanel workflowId={workflowId} isNew={isNew} />
            </div>
          </aside>
        )}
      </div>

      {showExecutionPanel && (
        <ExecutionPanel
          status={executionStatus}
          polling={polling}
          onClose={() => setShowExecutionPanel(false)}
        />
      )}
    </div>
  );
}
